/**
 * CashflowValidationModal
 *
 * Modal to display cashflow sanity check results.
 * Shows validation errors and warnings in a clear format.
 */

import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Heading,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';

import type { CashflowValidationResult, ValidationIssue } from '~/services/cashflow-validation';

interface CashflowValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: CashflowValidationResult | null;
  candidateName?: string;
}

export const CashflowValidationModal: React.FC<CashflowValidationModalProps> = ({
  isOpen,
  onClose,
  result,
  candidateName,
}) => {
  if (!result) return null;

  const { passed, totalRows, errors, warnings, issues } = result;

  const errorIssues = issues.filter((i) => i.severity === 'error');
  const warningIssues = issues.filter((i) => i.severity === 'warning');

  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="xl">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxH="80vh" overflow="hidden">
          <Dialog.Header>
            <Dialog.Title>
              <Flex align="center" gap={2}>
                {passed ? (
                  <CheckCircle size={20} color="var(--chakra-colors-green-500)" />
                ) : (
                  <AlertCircle size={20} color="var(--chakra-colors-red-500)" />
                )}
                Cashflow Sanity Check
              </Flex>
            </Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Header>

          <Dialog.Body overflowY="auto">
            <VStack align="stretch" gap={4}>
              {/* Summary */}
              <Box
                p={4}
                borderRadius="md"
                bg={passed ? 'green.50' : 'red.50'}
                borderWidth={1}
                borderColor={passed ? 'green.200' : 'red.200'}
              >
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="sm" color={passed ? 'green.700' : 'red.700'}>
                      {passed ? 'All Checks Passed' : 'Validation Failed'}
                    </Heading>
                    {candidateName && (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        Candidate: {candidateName}
                      </Text>
                    )}
                  </Box>
                  <Stack direction="row" gap={2}>
                    <Badge colorPalette="gray">{totalRows} rows</Badge>
                    <Badge colorPalette={errors > 0 ? 'red' : 'green'}>{errors} errors</Badge>
                    <Badge colorPalette={warnings > 0 ? 'yellow' : 'gray'}>
                      {warnings} warnings
                    </Badge>
                  </Stack>
                </Flex>
              </Box>

              {/* Errors */}
              {errorIssues.length > 0 && (
                <Box>
                  <Heading size="xs" mb={2} color="red.600">
                    <Flex align="center" gap={1}>
                      <AlertCircle size={14} />
                      Errors ({errorIssues.length})
                    </Flex>
                  </Heading>
                  <VStack align="stretch" gap={2}>
                    {errorIssues.slice(0, 20).map((issue) => (
                      <IssueRow
                        key={`${issue.paymentDate}-${issue.field}-${issue.rowIndex}`}
                        issue={issue}
                      />
                    ))}
                    {errorIssues.length > 20 && (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        ... and {errorIssues.length - 20} more errors
                      </Text>
                    )}
                  </VStack>
                </Box>
              )}

              {/* Warnings */}
              {warningIssues.length > 0 && (
                <Box>
                  <Heading size="xs" mb={2} color="yellow.600">
                    <Flex align="center" gap={1}>
                      <AlertTriangle size={14} />
                      Warnings ({warningIssues.length})
                    </Flex>
                  </Heading>
                  <VStack align="stretch" gap={2}>
                    {warningIssues.slice(0, 10).map((issue) => (
                      <IssueRow
                        key={`warn-${issue.paymentDate}-${issue.field}-${issue.rowIndex}`}
                        issue={issue}
                      />
                    ))}
                    {warningIssues.length > 10 && (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">
                        ... and {warningIssues.length - 10} more warnings
                      </Text>
                    )}
                  </VStack>
                </Box>
              )}

              {/* Success message when no issues */}
              {passed && issues.length === 0 && (
                <Box p={4} textAlign="center">
                  <CheckCircle size={48} color="var(--chakra-colors-green-500)" />
                  <Text mt={2} color="gray.600">
                    All mathematical identities verified. Cashflow data is consistent.
                  </Text>
                </Box>
              )}
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <Button onClick={onClose}>Close</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

// Individual issue row component
const IssueRow: React.FC<{ issue: ValidationIssue }> = ({ issue }) => {
  const isError = issue.severity === 'error';

  return (
    <Box
      p={2}
      borderRadius="sm"
      bg={isError ? 'red.50' : 'yellow.50'}
      borderLeftWidth={3}
      borderLeftColor={isError ? 'red.400' : 'yellow.400'}
      fontSize="sm"
    >
      <Flex justify="space-between" align="start">
        <Box flex={1}>
          <Text fontFamily="mono" fontSize="xs" color="gray.500">
            {issue.paymentDate && `Row ${(issue.rowIndex ?? 0) + 1}: ${issue.paymentDate}`}
            {issue.field && ` - ${issue.field}`}
          </Text>
          <Text color={isError ? 'red.700' : 'yellow.700'}>{issue.message}</Text>
        </Box>
      </Flex>
    </Box>
  );
};
